import { useState } from 'react'
import { Box, Chip, Paper, Typography } from '@mui/material'
import CompressIcon from '@mui/icons-material/Compress'
import type { Message } from 'chat-shared'
import { MarkdownContent } from './MarkdownContent.js'
import { ReasoningPanel } from './ReasoningPanel.js'
import { ToolChip } from './ToolChip.js'
import type { StreamingToolCall } from '../chat/useChatSession.js'

const toDisplayToolCalls = (
  message: Message,
  allMessages: Array<Message>,
): Array<StreamingToolCall> =>
  (message.toolCalls ?? []).map((call) => {
    const resultMessage = allMessages.find(
      (m) => m.role === 'tool' && m.toolResults?.at(0)?.toolCallId === call.id,
    )
    const toolResult = resultMessage?.toolResults?.at(0)
    return {
      toolCallId: call.id,
      toolName: call.name,
      args: call.args,
      status: !toolResult ? 'running' : toolResult.isError ? 'error' : 'done',
      resultPreview:
        toolResult && typeof toolResult.result === 'string' ? toolResult.result : undefined,
    }
  })

// Ab hier wurde der Kontext komprimiert: Der Verlauf darüber geht nicht mehr
// an das Modell, stattdessen die (aufklappbare) Zusammenfassung.
const CompactionMarker = ({ message }: { message: Message }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <Box display="flex" flexDirection="column" alignItems="center" mb={2} gap={1}>
      <Chip
        icon={<CompressIcon />}
        label={expanded ? 'Kontext komprimiert – Zusammenfassung ausblenden' : 'Kontext komprimiert – Zusammenfassung anzeigen'}
        size="small"
        variant="outlined"
        onClick={() => setExpanded((prev) => !prev)}
      />
      {expanded && (
        <Paper
          elevation={0}
          sx={{ maxWidth: '75%', p: 1.5, border: '1px dashed', borderColor: 'divider' }}
        >
          <MarkdownContent content={message.content} />
        </Paper>
      )}
    </Box>
  )
}

export const MessageBubble = ({
  message,
  allMessages,
}: {
  message: Message
  allMessages: Array<Message>
}) => {
  if (message.role === 'tool') return null
  if (message.role === 'summary') return <CompactionMarker message={message} />

  const isUser = message.role === 'user'
  const toolCalls = message.role === 'assistant' ? toDisplayToolCalls(message, allMessages) : []

  return (
    <Box display="flex" justifyContent={isUser ? 'flex-end' : 'flex-start'} mb={2}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: '75%',
          p: 1.5,
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider',
        }}
      >
        {!isUser && message.reasoning && (
          <ReasoningPanel reasoning={message.reasoning} hasFinalText={message.content.length > 0} />
        )}
        {toolCalls.length > 0 && (
          <Box mb={1}>
            {toolCalls.map((call) => (
              <ToolChip key={call.toolCallId} call={call} />
            ))}
          </Box>
        )}
        {message.content.length > 0 &&
          (isUser ? (
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
          ) : (
            <MarkdownContent content={message.content} />
          ))}
        {message.aborted && (
          <Typography variant="caption" color="text.secondary">
            (abgebrochen)
          </Typography>
        )}
      </Paper>
    </Box>
  )
}
